import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { BleDevice } from '@capacitor-community/bluetooth-le';
import { ModalController } from '@ionic/angular';
import { InputAppClientDBFacade } from '@monorepo/core-data';
import { BleService, DevicesService } from '@monorepo/services';
import { TDevicesDB, TRfidDevice } from '@monorepo/types';
import { map, take } from 'rxjs/operators';
import { UpdateReadpowerModalComponent } from '../../device-management/update-readpower-modal/update-readpower-modal.component';
import { combineLatest, Observable, Subscription } from 'rxjs';

@Component({
  selector: 'monorepo-ble-mode',
  templateUrl: './ble-mode.component.html',
  styleUrls: ['./ble-mode.component.scss'],
})
export class BleModeComponent implements OnInit, OnDestroy {
  @Input() showOnlyRfidOptions = false; // true for ba-app, no configuration available.

  readPower: number = 500;
  backArrow = 'assets/common/go-back.svg';
  allAvailableRfidVendors: TRfidDevice['vendor'][] = ['NEWLAND', 'CHAINWAY', 'APULSETECH'];
  vendorToDisplayNameMap: Record<TRfidDevice['vendor'], string> = {
    NEWLAND: 'ble_mode.newlandPlugin',
    CHAINWAY: 'ble_mode.chainwayIntegrated',
    APULSETECH: 'ble_mode.apulsetechPlugin',
  };
  isMultiMode: boolean = true;
  useRfidScanMode: boolean = false;
  connectedVendor: TRfidDevice['vendor'] | null = null;
  allowZebraBleConnection: boolean = false;
  isZebraBleDeviceConnected: boolean = false;

  subscriptions$: Subscription[] = [];

  constructor(
    public bleService: BleService,
    public modalCtrl: ModalController,
    private inputAppClientDBFacade: InputAppClientDBFacade,
    private devicesService: DevicesService,
  ) {}

  ngOnInit(): void {
    const config$ = this.inputAppClientDBFacade.getConfigurations().pipe(take(1));
    const connectedDevice$ = this.devicesService.getConnectedDeviceInfo();
    type TCombinedResult = {
      config: any | null;
      res: TDevicesDB['connectedDeviceInfo'];
    };
    const combined$: Observable<TCombinedResult> = this.showOnlyRfidOptions
      ? connectedDevice$.pipe(
          map((res) => ({
            config: null,
            res,
          })),
        )
      : combineLatest([config$, connectedDevice$]).pipe(
          map(([config, res]) => ({
            config,
            res,
          })),
        );

    const s1$ = combined$.subscribe(({ config, res }) => {
      if (config) {
        const { useRFIDScanMode, readPowerOfRFID: readPowerOfRFIDFromConfig, allowZebraBleConnection } = config;
        this.useRfidScanMode = useRFIDScanMode;
        this.allowZebraBleConnection = allowZebraBleConnection;
        if (this.allowZebraBleConnection) this.devicesService.setZebraEnabled(true);

        if (!res.RFID) {
          this.readPower = readPowerOfRFIDFromConfig;
        }
      }

      if (res.RFID) {
        const { vendor, readPower: userSetDeviceReadPower } = res.RFID;
        this.connectedVendor = vendor;
        this.readPower = userSetDeviceReadPower;
      } else {
        this.connectedVendor = null;
      }
    });

    const s2$ = this.devicesService.getZebraBleModeDeviceConnectionSubscription().subscribe((res) => {
      this.isZebraBleDeviceConnected = res.connectionState === 'CONNECTED';
    });
    this.subscriptions$.push(s1$, s2$);
  }

  ngOnDestroy(): void {
    this.subscriptions$.forEach((s) => s.unsubscribe());
  }

  async turnOnBluetoothAndLocation($event: any) {
    if ($event.detail.checked) {
      await this.bleService.enableBluetooth();
      const isLocation = await this.bleService.isLocationEnabled();
      if (!isLocation) await this.bleService.showLocationAlert();
    }
  }

  goBack() {
    this.modalCtrl.dismiss();
  }

  async scanDevices() {
    await this.bleService.updateAvailableDevicesList();
  }

  connectToDevice(bleDevice: BleDevice) {
    this.bleService.connectToDeviceAndStartNotifications(bleDevice, false);
  }

  disconnectToDevice(bleDevice: BleDevice) {
    this.bleService.onUserDisconnectDevice(bleDevice.deviceId);
  }

  unpairDevice(bleDevice: BleDevice) {
    this.bleService.unpairDevice(bleDevice.deviceId);
  }

  handleConnectToRfid(vendor: TRfidDevice['vendor']) {
    const { minimumSupportedRange, maximumSupportedRange } =
      this.getMinimumAndMaximumSupportedRangeForTheVendorBasedRfidDevice(vendor);
    if (this.readPower < minimumSupportedRange || this.readPower > maximumSupportedRange) {
      this.readPower = maximumSupportedRange;
    }
    this.devicesService.connectToVendorBasedRfidDevice(vendor, this.readPower);
  }

  handleDisconnectRfid() {
    this.devicesService.disconnectVendorBasedRfidDevice();
  }

  async openUpdateReadpowerModal() {
    if (!this.connectedVendor) return;
    const modal = await this.modalCtrl.create({
      component: UpdateReadpowerModalComponent,
      backdropDismiss: false,
      cssClass: 'update-readpower-modal',
      componentProps: this.#getComponentPropsForReadpowerUpdate(),
    });
    modal.present();
    modal.onDidDismiss().then((res) => {
      if (res.data !== null && res.data !== undefined) {
        this.devicesService.updateReadpower(res.data);
      }
    });
  }

  #getComponentPropsForReadpowerUpdate(): TUpdateReadpowerProps {
    const vendor = this.connectedVendor as TRfidDevice['vendor'];
    const propsWithReadpower: TUpdateReadpowerProps = {
      connectedVendor: vendor,
      ...this.getMinimumAndMaximumSupportedRangeForTheVendorBasedRfidDevice(vendor),
      readpower: this.readPower,
    };
    return propsWithReadpower;
  }

  getMinimumAndMaximumSupportedRangeForTheVendorBasedRfidDevice(vendor: TRfidDevice['vendor']): {
    minimumSupportedRange: number;
    maximumSupportedRange: number;
  } {
    switch (vendor) {
      case 'APULSETECH': {
        return {
          minimumSupportedRange: 0,
          maximumSupportedRange: 30,
        };
      }
      case 'CHAINWAY': {
        return {
          minimumSupportedRange: 1,
          maximumSupportedRange: 30,
        };
      }
      case 'NEWLAND': {
        return {
          minimumSupportedRange: 1,
          maximumSupportedRange: 30,
        };
      }
    }
  }

  connectToZebraDeviceUsingBleMode() {
    this.devicesService.connectToZebraScannerUsingBleMode();
  }

  disconnectZebraBleModeDevice() {
    this.devicesService.disconnectZebraBleModeDevice();
  }
}

type TUpdateReadpowerProps = {
  connectedVendor: TRfidDevice['vendor'];
  minimumSupportedRange: number;
  maximumSupportedRange: number;
  readpower: number;
};
